/**
 * Блок «Размещение сезона (демо)» — общий для карточки контура и карточки
 * хозяйства.
 *
 * Компонент один намеренно: это одни и те же гектары, и показывать их
 * по-разному в зависимости от того, с какой стороны на них смотрят, значило бы
 * заводить два источника правды об одном факте. Различается только ось —
 * контур смотрит «кто и что здесь сеет», хозяйство — «где лежат его культуры».
 *
 * Блока нет, когда сервер не прислал ни одной строки. А не прислал он их ровно
 * тогда, когда синтетику не запрашивали: своей проверки `include_demo` здесь
 * нет и быть не должно — иначе рядом с серверным правилом появилось бы второе,
 * клиентское, и однажды они разойдутся.
 */

import { Link } from "react-router-dom";
import type { PlacementDetailBlock } from "../../api/client";
import {
  Area,
  Badge,
  Card,
  EvidenceBadge,
  InlineAlert,
  ProvenanceLine,
} from "../../components/ui";
import { cropLabel } from "../../lib/crops";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import styles from "../features.module.css";

const m = defineMessages({
  title: { id: "placement.detail.title", defaultMessage: "Размещение {season}", description: "Заголовок карточки в карточке контура/хозяйства (демо-данные): размещение культур в сезоне; {season} — год; рядом бейдж «ДЕМО»" },
  demo: { id: "placement.detail.demo", defaultMessage: "ДЕМО", description: "Бейдж рядом с заголовком блока: данные синтетические; заглавными, коротко" },
  alertTitle: { id: "placement.detail.alertTitle", defaultMessage: "Синтетическая детализация — выведена, а не заявлена", description: "Заголовок предупреждения над таблицами размещения (демо): данные выведены генератором; пояснение приходит с сервера" },
  cropsCaption: { id: "placement.detail.cropsCaption", defaultMessage: "Итог по культурам этого блока. Складывать его с фактическими мерами размещения нельзя: меры отвечают на вопрос «сколько», а детализация — на вопрос «где».", description: "Подпись (caption) таблицы итога по культурам в демо-блоке размещения" },
  colCrop: { id: "placement.detail.colCrop", defaultMessage: "Культура", description: "Заголовок колонки таблицы размещения" },
  colArea: { id: "placement.detail.colArea", defaultMessage: "Площадь, га", description: "Заголовок числовой колонки таблицы размещения: площадь в гектарах" },
  colContours: { id: "placement.detail.colContours", defaultMessage: "Контуров", description: "Заголовок числовой колонки итога по культурам: число контуров; узкая колонка" },
  totalDemo: { id: "placement.detail.totalDemo", defaultMessage: "Всего (демо)", description: "Подпись итоговой строки таблицы по культурам в демо-блоке" },
  rowsCaption: { id: "placement.detail.rowsCaption", defaultMessage: "Строки детализации. «Занято частично» означает, что остаток посевной площади этой единицы землепользования остался свободным.", description: "Подпись (caption) таблицы строк демо-размещения" },
  colContour: { id: "placement.detail.colContour", defaultMessage: "Контур", description: "Заголовок первой колонки таблицы в карточке хозяйства: контур" },
  colFarm: { id: "placement.detail.colFarm", defaultMessage: "Хозяйство", description: "Заголовок первой колонки таблицы в карточке контура: хозяйство" },
  colOccupation: { id: "placement.detail.colOccupation", defaultMessage: "Занятие", description: "Заголовок колонки: занята ли единица землепользования целиком или частично" },
  colClass: { id: "placement.detail.colClass", defaultMessage: "Класс", description: "Заголовок колонки: класс достоверности данных строки (бейдж)" },
  colSource: { id: "placement.detail.colSource", defaultMessage: "Происхождение", description: "Заголовок колонки: откуда взята строка" },
  farmFallback: { id: "placement.detail.farmFallback", defaultMessage: "хозяйство #{id}", description: "Имя хозяйства в ссылке, когда названия нет; {id} — номер хозяйства" },
  unmatched: { id: "placement.detail.unmatched", defaultMessage: "не сопоставлено", description: "Ячейка «Хозяйство»: хозяйство строки не найдено в реестре" },
  partHint: { id: "placement.detail.partHint", defaultMessage: "Землепользование на части контура", description: "Подсказка к бейджу части контура" },
  part: { id: "placement.detail.part", defaultMessage: "часть {label}", description: "Бейдж: землепользование на части контура; {label} — метка части; коротко" },
  occupationUnknown: { id: "placement.detail.occupationUnknown", defaultMessage: "неизвестно", description: "Ячейка «Занятие»: неизвестно, занята единица целиком или частично; строчная" },
  occupationPartial: { id: "placement.detail.occupationPartial", defaultMessage: "занято частично", description: "Ячейка «Занятие»: культура занимает часть единицы землепользования; строчная" },
  occupationFull: { id: "placement.detail.occupationFull", defaultMessage: "занято целиком", description: "Ячейка «Занятие»: культура занимает единицу землепользования целиком; строчная" },
  batch: {
    id: "placement.detail.batch",
    defaultMessage: "Партия #{id}: генератор {generator}, нормативы {norms}, seed {seed}. Тот же вход и тот же seed дают тот же результат; партия удаляется целиком, не задевая реальных записей.",
    description: "Подпись под демо-блоком: служебные сведения о партии синтетических данных; {id} — номер партии, {generator} и {norms} — версии, {seed} — число; технические значения не переводятся",
  },
});

export interface DemoPlacementDetailProps {
  blocks: ReadonlyArray<PlacementDetailBlock>;
  /** Что показывать в первой колонке: контур (в карточке хозяйства) или
      хозяйство (в карточке контура). */
  axis: "contour" | "actor";
  /** Поисковая строка контекста анализа для ссылок между карточками. */
  linkSearch: string;
}

export function DemoPlacementDetail({
  blocks,
  axis,
  linkSearch,
}: DemoPlacementDetailProps) {
  const t = useT();
  if (blocks.length === 0) return null;

  return (
    <>
      {blocks.map((block) => (
        <Card
          key={block.season}
          title={
            <>
              {t(m.title, { season: block.season })} <Badge tone="demo">{t(m.demo)}</Badge>
            </>
          }
        >
          <InlineAlert title={t(m.alertTitle)}>
            {block.demo_note}
          </InlineAlert>

          <table className={styles.plainTable}>
            <caption>
              {t(m.cropsCaption)}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t(m.colCrop)}</th>
                <th scope="col" className={styles.num}>
                  {t(m.colArea)}
                </th>
                <th scope="col" className={styles.num}>
                  {t(m.colContours)}
                </th>
              </tr>
            </thead>
            <tbody>
              {block.by_crop.map((crop) => {
                const label = cropLabel(crop.crop_code);
                return (
                  <tr key={crop.crop_code}>
                    <td>
                      {label.known ? (
                        label.label
                      ) : (
                        <Badge tone="unknown">{label.label}</Badge>
                      )}
                    </td>
                    <td className={styles.num}>
                      <Area microha={crop.area_microha} unit={null} />
                    </td>
                    <td className={styles.num}>{crop.contours}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">{t(m.totalDemo)}</th>
                <td className={styles.num}>
                  <Area microha={block.total_microha} unit={null} />
                </td>
                <td />
              </tr>
            </tfoot>
          </table>

          <div className={styles.scrollX}>
            <table className={styles.plainTable}>
              <caption>
                {t(m.rowsCaption)}
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    {axis === "actor" ? t(m.colContour) : t(m.colFarm)}
                  </th>
                  <th scope="col">{t(m.colCrop)}</th>
                  <th scope="col" className={styles.num}>
                    {t(m.colArea)}
                  </th>
                  <th scope="col">{t(m.colOccupation)}</th>
                  <th scope="col">{t(m.colClass)}</th>
                  <th scope="col">{t(m.colSource)}</th>
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {axis === "actor" ? (
                        <Link
                          to={{
                            pathname: `/contours/${row.contour_id}`,
                            search: linkSearch,
                          }}
                        >
                          {row.contour_no}
                        </Link>
                      ) : row.actor ? (
                        <Link
                          to={{
                            pathname: `/actors/${row.actor.id}`,
                            search: linkSearch,
                          }}
                        >
                          {row.actor.name ?? t(m.farmFallback, { id: row.actor.id })}
                        </Link>
                      ) : (
                        t(m.unmatched)
                      )}
                      {row.part_label ? (
                        <div className={styles.rowWrap}>
                          <Badge title={t(m.partHint)}>
                            {t(m.part, { label: row.part_label })}
                          </Badge>
                        </div>
                      ) : null}
                    </td>
                    <td>{cropLabel(row.crop_code).label}</td>
                    <td className={styles.num}>
                      <Area microha={row.area_microha} unit={null} />
                    </td>
                    <td>
                      {/* «Целиком» здесь про ЕДИНИЦУ землепользования: у
                          нарезанного контура это часть, а не весь контур. */}
                      {row.partial_contour === null
                        ? t(m.occupationUnknown)
                        : row.partial_contour
                          ? t(m.occupationPartial)
                          : t(m.occupationFull)}
                    </td>
                    <td>
                      <EvidenceBadge status={row.epistemic_status} />
                    </td>
                    <td>
                      <ProvenanceLine source={row.source} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {block.batches.map((batch) => (
            <p className={styles.demoBatch} key={batch.id}>
              {t(m.batch, {
                id: batch.id,
                generator: batch.generator_version,
                norms: batch.norms_version,
                seed: batch.seed,
              })}
            </p>
          ))}
        </Card>
      ))}
    </>
  );
}
